import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CircleIcon from "@mui/icons-material/Circle";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import codService, { deriveCodReviewStatus } from "../../../services/CodService";
import salesManagerEnrolmentService from "../../../services/SalesManagerEnrolmentService";
import {
  CodApprovalStatus,
  CodManagerTab,
  CodOrder,
  CodReviewStatus,
} from "../../../types/codSalesRep";
import { SalesManagerRepSummary } from "../../../types/salesManagerEnrolment";
import { showToast } from "../../component/common/toastNotification";
import {
  formatCodAmount,
  formatCodDate,
} from "../../../utils/codUtils";
import {
  NJIM,
  pageShellSx,
  successBtnSx,
  dangerOutlinedBtnSx,
  iconBtnSx,
  secondaryBtnSx,
  dangerBtnSx,
  codTableSx,
} from "../codSalesRep/codStyles";
import SearchField from "../../component/common/searchField";
import CodTabBar from "../codSalesRep/CodTabBar";
import PageHeader from "../../../components/ui/PageHeader";
import CodStatCard from "../../../components/ui/CodStatCard";
import StatusChip from "../../../components/ui/StatusChip";
import EmptyState from "../../../components/ui/EmptyState";
import CodInitialsAvatar from "../../../components/ui/CodInitialsAvatar";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";

const formatCustomerLabel = (order: CodOrder) => {
  const name = order.customerName?.trim();
  const code = order.customerCode?.trim();
  if (name && code) return `${name} (${code})`;
  return name || code || "—";
};

const normalizeRepQuery = (value: string) =>
  value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/[إأآٱا]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[^\w\u0600-\u06FF]+/g, " ")
    .trim();

const matchesRepSearch = (rep: SalesManagerRepSummary, query: string) => {
  const keyword = normalizeRepQuery(query);
  if (!keyword) return true;

  const haystack = normalizeRepQuery(
    [rep.salesRepName, rep.salesRepCode, rep.repRole].filter(Boolean).join(" ")
  );
  if (haystack.includes(keyword)) return true;

  const digits = query.replace(/\D/g, "");
  if (digits) {
    const codeDigits = (rep.salesRepCode ?? "").replace(/\D/g, "");
    if (codeDigits && (codeDigits.includes(digits) || digits.includes(codeDigits))) {
      return true;
    }
    const codeNum = Number(rep.salesRepCode);
    const queryNum = Number(digits);
    if (!Number.isNaN(codeNum) && !Number.isNaN(queryNum) && codeNum === queryNum) {
      return true;
    }
  }

  return false;
};

// Approved orders show their PAYMENT state on the label; Sage sync is shown separately as a dot.
type CardStatus = "pendingReview" | "rejected" | "paid" | "partlyPaid" | "awaitingPayment";

const toCardStatus = (status: CodReviewStatus): CardStatus => {
  if (status === "pendingReview" || status === "rejected" || status === "paid" || status === "partlyPaid") {
    return status;
  }
  return "awaitingPayment"; // approved / postedToSage with no cash collected yet
};

const statusBorderColor = (status: CardStatus) => {
  if (status === "pendingReview") return NJIM.warning;
  if (status === "paid") return NJIM.success;
  if (status === "partlyPaid") return NJIM.warning;
  if (status === "rejected") return NJIM.danger;
  return NJIM.info;
};

export const deriveReviewStatus = deriveCodReviewStatus;

const teamRowSx = (selected: boolean) =>
  ({
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    width: "100%",
    boxSizing: "border-box",
    minHeight: 48,
    px: 1.25,
    py: 0.75,
    borderRadius: "10px",
    border: "1px solid transparent",
    borderColor: selected ? NJIM.mint : "transparent",
    background: selected ? NJIM.mintWash : "transparent",
    cursor: "pointer",
    font: "inherit",
    position: "relative",
    transition: `background-color 150ms ${NJIM.easeOut}, border-color 150ms ${NJIM.easeOut}`,
    "&:hover": { background: selected ? NJIM.mintWash : NJIM.muted },
    "&:focus-visible": { outline: `2px solid ${NJIM.mint}`, outlineOffset: 1 },
  }) as const;

const pendingDotSx = {
  minWidth: 20,
  height: 20,
  px: 0.75,
  borderRadius: "999px",
  bgcolor: NJIM.warning,
  color: NJIM.white,
  fontSize: 11,
  fontWeight: 700,
  lineHeight: "20px",
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
  boxSizing: "border-box",
} as const;

interface RejectDialogState {
  open: boolean;
  orderId: string;
  orderNumber: string;
}

const CodSalesManager: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState("");
  const [orders, setOrders] = useState<CodOrder[]>([]);
  const [activeTab, setActiveTab] = useState<CodManagerTab>("pendingReview");
  const [search, setSearch] = useState("");
  const [repFilter, setRepFilter] = useState("all");
  const [expandedId, setExpandedId] = useState("");
  const [rejectDialog, setRejectDialog] = useState<RejectDialogState>({
    open: false,
    orderId: "",
    orderNumber: "",
  });
  const [rejectReason, setRejectReason] = useState("");
  const [myReps, setMyReps] = useState<SalesManagerRepSummary[]>([]);
  const [managerSetupError, setManagerSetupError] = useState("");
  const [repSearch, setRepSearch] = useState("");

  const loadOrders = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    // Start "my reps" in parallel with the orders instead of waiting for the orders first.
    const repsRequest = salesManagerEnrolmentService.getMyReps().then(
      (reps) => ({ ok: true as const, reps }),
      (error: unknown) => ({ ok: false as const, error })
    );
    try {
      const rows = await codService.listManagerOrders();
      setOrders(rows);
      try {
        const repsResult = await repsRequest;
        if (!repsResult.ok) throw repsResult.error;
        const reps = repsResult.reps;
        setMyReps(reps);
        setManagerSetupError("");
      } catch (error) {
        const status = (error as { response?: { status?: number; data?: { message?: string } } })
          ?.response?.status;
        const message = String(
          (error as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? ""
        );
        if (status === 400 || /salesmanagercode/i.test(message)) {
          setManagerSetupError(t("codSalesManager.salesManagerCodeMissing"));
        }
        setMyReps([]);
      }
    } catch {
      if (!opts?.silent) showToast(t("codSalesManager.loadFailed"), "error");
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const sortedReps = useMemo(
    () =>
      [...myReps].sort((a, b) =>
        (a.salesRepName || a.salesRepCode).localeCompare(
          b.salesRepName || b.salesRepCode,
          undefined,
          { sensitivity: "base" }
        )
      ),
    [myReps]
  );

  const visibleReps = useMemo(
    () => sortedReps.filter((rep) => matchesRepSearch(rep, repSearch)),
    [sortedReps, repSearch]
  );

  const repOptions = useMemo(() => {
    const fromReps = myReps
      .map((rep) => rep.salesRepName?.trim())
      .filter(Boolean) as string[];
    if (fromReps.length > 0) return Array.from(new Set(fromReps)).sort();
    const reps = new Set<string>();
    orders.forEach((order) => {
      if (order.salesRepName?.trim()) reps.add(order.salesRepName.trim());
    });
    return Array.from(reps).sort();
  }, [myReps, orders]);

  const enrichedOrders = useMemo(
    () =>
      orders.map((order) => ({
        ...order,
        derivedStatus: deriveReviewStatus(order),
      })),
    [orders]
  );

  // KPIs and tab counts follow the selected rep (display only).
  const repScopedOrders = useMemo(
    () =>
      repFilter === "all"
        ? enrichedOrders
        : enrichedOrders.filter((order) => order.salesRepName === repFilter),
    [enrichedOrders, repFilter]
  );

  const counts = useMemo(() => {
    const pending = repScopedOrders.filter((o) => o.derivedStatus === "pendingReview");
    const approved = repScopedOrders.filter(
      (o) =>
        o.derivedStatus === "approved" ||
        o.derivedStatus === "postedToSage" ||
        o.derivedStatus === "paid" ||
        o.derivedStatus === "partlyPaid"
    );
    const rejected = repScopedOrders.filter((o) => o.derivedStatus === "rejected");
    const pendingValue = pending.reduce((sum, o) => sum + Number(o.amount ?? 0), 0);
    return {
      pendingReview: pending.length,
      approved: approved.length,
      postedToSage: repScopedOrders.filter((o) => o.derivedStatus === "postedToSage").length,
      rejected: rejected.length,
      pendingValue,
      pendingReps: Array.from(
        new Set(pending.map((o) => o.salesRepName).filter(Boolean))
      ) as string[],
    };
  }, [repScopedOrders]);

  // Display-only: pending / total order counts per rep name for the team list.
  const repStats = useMemo(() => {
    const map = new Map<string, { pending: number; total: number }>();
    enrichedOrders.forEach((order) => {
      const name = order.salesRepName?.trim();
      if (!name) return;
      const entry = map.get(name) ?? { pending: 0, total: 0 };
      entry.total += 1;
      if (order.derivedStatus === "pendingReview") entry.pending += 1;
      map.set(name, entry);
    });
    return map;
  }, [enrichedOrders]);

  const allPendingCount = useMemo(
    () => enrichedOrders.filter((o) => o.derivedStatus === "pendingReview").length,
    [enrichedOrders]
  );

  // Unified team list: enrolled reps when available, otherwise reps seen on orders.
  const teamList = useMemo(() => {
    if (myReps.length > 0) {
      return visibleReps.map((rep) => ({
        name: (rep.salesRepName || rep.salesRepCode).trim(),
        code: rep.salesRepName ? rep.salesRepCode : "",
      }));
    }
    const keyword = repSearch.trim().toLowerCase();
    return repOptions
      .filter((name) => !keyword || name.toLowerCase().includes(keyword))
      .map((name) => ({ name, code: "" }));
  }, [myReps.length, visibleReps, repOptions, repSearch]);

  const teamTotal = myReps.length > 0 ? myReps.length : repOptions.length;

  const managerTabs = useMemo(
    () => [
      {
        value: "pendingReview" as const,
        label: t("codSalesRep.pendingReview"),
        count: counts.pendingReview,
        icon: <HourglassTopRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        value: "approved" as const,
        label: t("codSalesRep.approved"),
        count: counts.approved,
        icon: <CheckCircleRoundedIcon sx={{ fontSize: 18 }} />,
      },
      {
        value: "rejected" as const,
        label: t("codSalesRep.rejected"),
        count: counts.rejected,
        icon: <CancelRoundedIcon sx={{ fontSize: 18 }} />,
      },
    ],
    [t, counts.pendingReview, counts.approved, counts.rejected]
  );

  const filteredOrders = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return enrichedOrders.filter((order) => {
      if (activeTab === "pendingReview" && order.derivedStatus !== "pendingReview") {
        return false;
      }
      if (
        activeTab === "approved" &&
        !["approved", "postedToSage", "paid", "partlyPaid"].includes(order.derivedStatus)
      ) {
        return false;
      }
      if (activeTab === "rejected" && order.derivedStatus !== "rejected") {
        return false;
      }
      if (repFilter !== "all" && order.salesRepName !== repFilter) return false;
      if (!keyword) return true;
      return (
        order.number.toLowerCase().includes(keyword) ||
        (order.customerName ?? "").toLowerCase().includes(keyword) ||
        (order.customerCode ?? "").toLowerCase().includes(keyword)
      );
    });
  }, [enrichedOrders, activeTab, repFilter, search]);

  const markOrderApprovedLocally = (orderId: string) => {
    setOrders((prev) =>
      prev.map((order) =>
        order.id === orderId
          ? { ...order, approvalStatus: 2 as CodApprovalStatus }
          : order
      )
    );
    setExpandedId("");
    setActiveTab("approved");
  };

  const isLikelyApprovedWithoutResponse = (error: unknown) => {
    const axiosErr = error as { code?: string; response?: { status?: number }; message?: string };
    if (axiosErr.response?.status) return false;
    const code = String(axiosErr.code ?? "");
    const message = String(axiosErr.message ?? "").toLowerCase();
    return (
      code === "ECONNABORTED" ||
      code === "ERR_NETWORK" ||
      message.includes("timeout") ||
      message.includes("network")
    );
  };

  const handleApprove = async (orderId: string) => {
    setActionLoading(orderId);
    try {
      await codService.approveOrder(orderId);
      showToast(t("codSalesManager.approveSuccess"), "success");
      markOrderApprovedLocally(orderId);
      await loadOrders({ silent: true });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        showToast(t("codSalesManager.alreadyDecided"), "info");
        await loadOrders({ silent: true });
      } else if (status === 403) {
        showToast(t("codSalesManager.forbiddenAction"), "error");
      } else if (isLikelyApprovedWithoutResponse(error)) {
        // Approve is saved before Sage; a hung/timeout response still means it left pending.
        showToast(t("codSalesManager.approveSuccess"), "success");
        markOrderApprovedLocally(orderId);
        await loadOrders({ silent: true });
      } else {
        const data = (error as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data;
        showToast(data?.message || data?.error || t("codSalesManager.loadFailed"), "error");
      }
    } finally {
      setActionLoading("");
    }
  };

  const openRejectDialog = (orderId: string, orderNumber: string) => {
    setRejectReason("");
    setRejectDialog({ open: true, orderId, orderNumber });
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return;
    setActionLoading(rejectDialog.orderId);
    try {
      await codService.rejectOrder(rejectDialog.orderId, rejectReason.trim());
      showToast(t("codSalesManager.rejectSuccess"), "success");
      setRejectDialog({ open: false, orderId: "", orderNumber: "" });
      await loadOrders({ silent: true });
      setExpandedId("");
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 409) {
        showToast(t("codSalesManager.alreadyDecided"), "info");
        await loadOrders({ silent: true });
      } else if (status === 403) {
        showToast(t("codSalesManager.forbiddenAction"), "error");
      } else {
        const data = (error as { response?: { data?: { message?: string; error?: string } } })
          ?.response?.data;
        showToast(data?.message || data?.error || t("codSalesManager.loadFailed"), "error");
      }
    } finally {
      setActionLoading("");
    }
  };

  const renderStatusBadge = (status: CardStatus) => {
    const map: Record<CardStatus, { label: string; tone: string }> = {
      pendingReview: { label: t("codSalesRep.pendingReview"), tone: "pendingReview" },
      rejected: { label: t("codSalesRep.rejected"), tone: "rejected" },
      paid: { label: t("codSalesRep.paid"), tone: "paid" },
      partlyPaid: { label: t("codSalesRep.partlyPaid"), tone: "partlyPaid" },
      awaitingPayment: { label: t("codSalesRep.awaitingPayment"), tone: "draft" },
    };
    const entry = map[status];
    return <StatusChip status={entry.tone} label={entry.label} />;
  };

  // Same indicator as the customer Order History: green = synced to Sage X3, yellow = not yet.
  const renderSyncDot = (order: CodOrder) => {
    const label = order.isSynced ? t("codSalesRep.syncedToSage") : t("codSalesRep.notSyncedToSage");
    return (
      <Tooltip title={label}>
        <CircleIcon
          aria-label={label}
          sx={{ color: order.isSynced ? NJIM.success : NJIM.warning, fontSize: 14, flexShrink: 0 }}
        />
      </Tooltip>
    );
  };

  const summaryCards = [
    {
      label: t("codSalesRep.pendingReview"),
      value: counts.pendingReview,
      tone: "warning" as const,
      icon: <HourglassTopRoundedIcon />,
    },
    {
      label: t("codSalesRep.postedToSage"),
      value: counts.postedToSage,
      tone: "success" as const,
      icon: <CloudDoneRoundedIcon />,
    },
    {
      label: t("codSalesRep.rejected"),
      value: counts.rejected,
      tone: "danger" as const,
      icon: <CancelRoundedIcon />,
    },
    {
      label: t("codSalesManager.totalValuePending"),
      value: formatCodAmount(counts.pendingValue),
      tone: "ink" as const,
      icon: <ReceiptLongRoundedIcon />,
      compactValue: true,
    },
  ];

  if (loading) {
    return (
      <Box sx={{ ...pageShellSx, maxWidth: 1280, mx: "auto" }}>
        <PageHeader title={t("codSalesManager.title")} subtitle={t("codSalesManager.subtitle")} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
            gap: 2,
            mb: 3,
          }}
        >
          {summaryCards.map((card) => (
            <CodStatCard key={card.label} label={card.label} value={card.value} tone={card.tone} icon={card.icon} loading />
          ))}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: "14px" }} />
          ))}
        </Box>
      </Box>
    );
  }

  const selectedRepCode =
    repFilter === "all"
      ? ""
      : myReps.find((rep) => (rep.salesRepName || rep.salesRepCode).trim() === repFilter)
          ?.salesRepCode ?? "";

  const renderTeamRow = (
    key: string,
    label: string,
    sub: string,
    selected: boolean,
    pending: number,
    total: number | null,
    onClick: () => void,
    avatar: React.ReactNode
  ) => (
    <Box component="li" key={key}>
      <Box
        component="button"
        type="button"
        onClick={onClick}
        aria-pressed={selected}
        sx={teamRowSx(selected)}
      >
        {avatar}
        <Box component="span" sx={{ minWidth: 0, flex: 1, textAlign: "start" }}>
          <Typography
            component="span"
            sx={{
              display: "block",
              fontSize: 13,
              fontWeight: selected ? 700 : 600,
              color: NJIM.textPrimary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              lineHeight: 1.3,
            }}
            title={label}
          >
            {label}
          </Typography>
          {sub ? (
            <Typography component="span" sx={{ display: "block", fontSize: 11, color: NJIM.textMuted, lineHeight: 1.3 }}>
              {sub}
            </Typography>
          ) : null}
        </Box>
        {pending > 0 ? (
          <Tooltip title={t("codSalesRep.pendingReview")}>
            <Box component="span" sx={pendingDotSx}>{pending}</Box>
          </Tooltip>
        ) : total != null && total > 0 ? (
          <Box component="span" sx={{ fontSize: 12, color: NJIM.textMuted, fontVariantNumeric: "tabular-nums", minWidth: 20, textAlign: "center" }}>
            {total}
          </Box>
        ) : null}
      </Box>
    </Box>
  );

  const teamPanel = (
    <Box
      sx={{
        display: { xs: "none", lg: "flex" },
        flexDirection: "column",
        position: "sticky",
        top: 88,
        maxHeight: "calc(100vh - 112px)",
        borderRadius: "16px",
        border: `1px solid ${NJIM.border}`,
        background: NJIM.white,
        boxShadow: NJIM.elevation,
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 2, pb: 1.5, borderBottom: `1px solid ${NJIM.border}` }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "10px",
              display: "grid",
              placeItems: "center",
              bgcolor: NJIM.mintWash,
              color: NJIM.ink,
              flexShrink: 0,
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: NJIM.ink, lineHeight: 1.3 }}>
              {t("codSalesManager.myRepsHeading")}
            </Typography>
            <Typography sx={{ fontSize: 12, color: NJIM.textSecondary }}>
              {t("codSalesManager.myRepsHint")}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={repSearch.trim() ? `${teamList.length}/${teamTotal}` : teamTotal}
            sx={{ height: 22, fontWeight: 700, bgcolor: NJIM.mintWash, color: NJIM.ink }}
          />
        </Box>
        <SearchField
          value={repSearch}
          onChange={setRepSearch}
          placeholder={t("codSalesManager.searchReps")}
        />
      </Box>

      <Box
        component="ul"
        sx={{
          m: 0,
          p: 1,
          listStyle: "none",
          overflowY: "auto",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {!repSearch.trim() &&
          renderTeamRow(
            "__all",
            t("codSalesManager.allReps"),
            "",
            repFilter === "all",
            allPendingCount,
            null,
            () => setRepFilter("all"),
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: repFilter === "all" ? NJIM.mint : NJIM.ink,
                color: NJIM.white,
                flexShrink: 0,
              }}
            >
              <GroupsOutlinedIcon sx={{ fontSize: 17 }} />
            </Box>
          )}
        {teamList.length === 0 ? (
          <Box component="li" sx={{ py: 3, textAlign: "center" }}>
            <Typography sx={{ fontSize: 13, color: NJIM.textMuted }}>
              {t("codSalesManager.noRepMatches")}
            </Typography>
          </Box>
        ) : (
          teamList.map((rep) => {
            const selected = repFilter === rep.name;
            const stats = repStats.get(rep.name);
            return renderTeamRow(
              rep.code || rep.name,
              rep.name,
              rep.code,
              selected,
              stats?.pending ?? 0,
              stats?.total ?? 0,
              () => setRepFilter(selected ? "all" : rep.name),
              <CodInitialsAvatar name={rep.name} size={32} selected={selected} />
            );
          })
        )}
      </Box>
    </Box>
  );

  const repAutocomplete = (
    <Autocomplete
      size="small"
      disableClearable
      options={["all", ...repOptions]}
      value={repFilter}
      onChange={(_, value) => setRepFilter(value ?? "all")}
      getOptionLabel={(option) => (option === "all" ? t("codSalesManager.allReps") : option)}
      isOptionEqualToValue={(option, value) => option === value}
      renderOption={(props, option) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key: string }; // eslint-disable-line react/prop-types
        const pending = option === "all" ? allPendingCount : repStats.get(option)?.pending ?? 0;
        return (
          <Box component="li" key={key} {...rest} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            {option === "all" ? (
              <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: NJIM.ink, color: NJIM.white, flexShrink: 0 }}>
                <GroupsOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
            ) : (
              <CodInitialsAvatar name={option} size={28} />
            )}
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {option === "all" ? t("codSalesManager.allReps") : option}
            </Typography>
            {pending > 0 && <Box component="span" sx={pendingDotSx}>{pending}</Box>}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={t("codSalesManager.searchReps")}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <GroupsOutlinedIcon sx={{ fontSize: 18, color: NJIM.textSecondary, marginInlineStart: "4px" }} />
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        display: { xs: "block", lg: "none" },
        width: { xs: "100%", sm: 280 },
        flexShrink: 0,
        "& .MuiOutlinedInput-root": {
          minHeight: 44,
          borderRadius: "10px",
          background: NJIM.white,
        },
      }}
    />
  );

  return (
    <Box sx={{ ...pageShellSx, maxWidth: 1440, mx: "auto" }}>
      <PageHeader
        title={t("codSalesManager.title")}
        subtitle={t("codSalesManager.subtitle")}
      />
      {managerSetupError && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {managerSetupError}
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: teamTotal > 0 ? "300px minmax(0, 1fr)" : "minmax(0, 1fr)" },
          gap: 3,
          alignItems: "start",
        }}
      >
        {teamTotal > 0 && teamPanel}

        <Box sx={{ minWidth: 0 }}>
          {/* Scope header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
              {repFilter === "all" ? (
                <Box sx={{ width: 40, height: 40, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: NJIM.ink, color: NJIM.white, flexShrink: 0 }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
              ) : (
                <CodInitialsAvatar name={repFilter} size={40} selected />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: NJIM.textSecondary, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                  {t("codSalesRep.overviewScope")}
                </Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: NJIM.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {repFilter === "all" ? t("codSalesManager.allReps") : repFilter}
                  {selectedRepCode ? (
                    <Box component="span" sx={{ fontSize: 13, fontWeight: 500, color: NJIM.textMuted, marginInlineStart: "8px" }}>
                      {selectedRepCode}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
              {repFilter !== "all" && (
                <Tooltip title={t("codSalesManager.allReps")}>
                  <IconButton
                    size="small"
                    aria-label={t("codSalesManager.allReps")}
                    onClick={() => setRepFilter("all")}
                    sx={{ ...iconBtnSx, marginInlineStart: 0.5 }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {repAutocomplete}
          </Box>

          <Box sx={{ containerType: "inline-size" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 2,
              mb: 3,
              "@container (min-width: 760px)": {
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {summaryCards.map((card) => (
              <CodStatCard
                key={card.label}
                label={card.label}
                value={card.value}
                tone={card.tone}
                icon={card.icon}
                compactValue={"compactValue" in card && card.compactValue}
              />
            ))}
          </Box>
          </Box>

          {counts.pendingReview > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 3,
                p: 2,
                borderRadius: "14px",
                border: `1px solid ${NJIM.warning}`,
                background: NJIM.warningWash,
              }}
            >
              <Box sx={{ width: 36, height: 36, borderRadius: "10px", display: "grid", placeItems: "center", bgcolor: NJIM.white, color: NJIM.warning, flexShrink: 0 }}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: NJIM.textPrimary }}>
                  {t("codSalesManager.awaitingReviewBanner", {
                    count: counts.pendingReview,
                  })}
                </Typography>
                <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, mt: 0.25 }}>
                  {t("codSalesManager.awaitingReviewDetail", {
                    value: formatCodAmount(counts.pendingValue),
                    reps: counts.pendingReps.join(", ") || "—",
                  })}
                </Typography>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", xl: "row" },
              alignItems: { xs: "stretch", xl: "center" },
              gap: 1.5,
              mb: 2,
              "& .MuiTab-root": { whiteSpace: "nowrap" },
              "& .MuiTab-root > span": { flexWrap: "nowrap" },
            }}
          >
            <Box sx={{ flexShrink: 0, "& > div": { mb: 0 } }}>
              <CodTabBar
                value={activeTab}
                tabs={managerTabs}
                onChange={setActiveTab}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <SearchField
                value={search}
                onChange={setSearch}
                placeholder={t("codSalesManager.searchPlaceholder")}
              />
            </Box>
          </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {filteredOrders.length === 0 ? (
          <EmptyState
            title={t("codSalesManager.emptyQueueTitle")}
            description={t("codSalesManager.emptyQueueDesc")}
            icon={<AssignmentRoundedIcon sx={{ fontSize: 26 }} />}
          />
        ) : (
          filteredOrders.map((order) => {
            const isOpen = expandedId === order.id;
            const cardStatus = toCardStatus(order.derivedStatus);
            const borderColor = statusBorderColor(cardStatus);
            const showSync = cardStatus !== "pendingReview" && cardStatus !== "rejected";

            return (
              <Box
                key={order.id}
                sx={{
                  borderRadius: "14px",
                  border: `1px solid ${NJIM.border}`,
                  background: NJIM.white,
                  borderInlineStart: `4px solid ${borderColor}`,
                  boxShadow: isOpen ? NJIM.elevationHover : NJIM.elevation,
                  overflow: "hidden",
                  transition: `box-shadow 180ms ${NJIM.easeOut}`,
                  "@media (prefers-reduced-motion: reduce)": {
                    transition: "none",
                  },
                }}
              >
                <Box
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => setExpandedId(isOpen ? "" : order.id)}
                >
                  <CodInitialsAvatar name={order.salesRepName || order.customerName} size={40} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 0.75,
                      }}
                    >
                      {showSync && renderSyncDot(order)}
                      <Typography sx={{ fontWeight: 800, fontSize: "15px", color: NJIM.textPrimary }}>
                        {order.number}
                      </Typography>
                      {order.salesRepName && (
                        <Box
                          sx={{
                            px: 1.1,
                            py: 0.25,
                            borderRadius: "999px",
                            fontSize: "11px",
                            fontWeight: 600,
                            color: NJIM.textSecondary,
                            background: NJIM.muted,
                          }}
                        >
                          {order.salesRepName}
                        </Box>
                      )}
                      {order.sageOrderNumbers?.[0] && (
                        <Typography variant="body2" sx={{ color: NJIM.ink, fontWeight: 600 }}>
                          → {order.sageOrderNumbers[0]}
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: NJIM.textPrimary,
                        mb: 0.5,
                      }}
                    >
                      {formatCustomerLabel(order)}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: NJIM.textMuted }}>
                      {t("codSalesManager.submitted")}{" "}
                      {formatCodDate(order.submittedAt ?? order.date, true)}
                      {(order.items?.length ?? 0) > 0 &&
                        ` · ${order.items!.length} ${t("codSalesManager.lines")}`}
                      {order.reviewedAt &&
                        ` · ${t("codSalesManager.reviewed")} ${formatCodDate(order.reviewedAt)}`}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
                    <Box sx={{ textAlign: "end" }}>
                      {renderStatusBadge(cardStatus)}
                      <Typography
                        sx={{
                          mt: 1,
                          fontWeight: 800,
                          fontSize: "18px",
                          color: NJIM.ink,
                          whiteSpace: "nowrap",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {formatCodAmount(order.amount, order.currency)}
                      </Typography>
                    </Box>
                    <Tooltip title={isOpen ? t("codSalesManager.collapseDetails") : t("codSalesManager.expandDetails")}>
                    <IconButton
                      size="small"
                      aria-label={isOpen ? t("codSalesManager.collapseDetails") : t("codSalesManager.expandDetails")}
                      sx={iconBtnSx}
                    >
                      {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Collapse in={isOpen}>
                  <Box
                    sx={{
                      px: { xs: 2, md: 2.5 },
                      pb: 2.5,
                      pt: 0,
                      borderTop: `1px solid ${NJIM.border}`,
                    }}
                  >
                    <Table size="small" sx={{ ...codTableSx, mt: 2 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("codSalesManager.product")}</TableCell>
                          <TableCell align="right">{t("common.quantity")}</TableCell>
                          <TableCell align="right">{t("common.unitPrice")}</TableCell>
                          <TableCell align="right">{t("codSalesManager.lineTotal")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(order.items ?? []).map((item, idx) => (
                          <TableRow key={`${order.id}-item-${idx}`}>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, color: NJIM.textPrimary, fontSize: "13px" }}>
                                {item.name}
                              </Typography>
                              {(item.code || item.productId) && (
                                <Typography sx={{ fontSize: "11px", color: NJIM.textMuted, mt: 0.25 }}>
                                  {item.code || item.productId}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {item.quantity}
                            </TableCell>
                            <TableCell align="right">
                              {formatCodAmount(item.price, order.currency)}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              {formatCodAmount(
                                item.grossPrice ?? item.price * item.quantity,
                                order.currency
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ background: NJIM.muted }}>
                          <TableCell colSpan={3} align="right" sx={{ fontWeight: 700, color: NJIM.textSecondary }}>
                            {t("codSalesManager.orderTotal")}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: NJIM.ink, fontSize: "15px" }}>
                            {formatCodAmount(order.amount, order.currency)}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>

                    {order.derivedStatus === "rejected" && order.rejectionReason && (
                      <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>
                        {order.rejectionReason}
                      </Alert>
                    )}

                    {order.derivedStatus === "pendingReview" && (
                      <Box
                        sx={{
                          mt: 2.5,
                          pt: 2,
                          borderTop: `1px solid ${NJIM.border}`,
                          display: "flex",
                          flexDirection: { xs: "column", md: "row" },
                          alignItems: { xs: "stretch", md: "center" },
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 18, color: NJIM.textMuted }} />
                          <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary }}>
                            {t("codSalesManager.noEditsHint")}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1.5,
                            justifyContent: { xs: "stretch", md: "flex-end" },
                            flexDirection: { xs: "column", sm: "row" },
                          }}
                        >
                          <Button
                            variant="outlined"
                            color="error"
                            disabled={!!actionLoading}
                            startIcon={<CloseIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              openRejectDialog(order.id, order.number);
                            }}
                            sx={dangerOutlinedBtnSx}
                          >
                            {t("codSalesManager.reject")}
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={
                              actionLoading === order.id ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : (
                                <CheckCircleOutlineIcon />
                              )
                            }
                            disabled={!!actionLoading}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprove(order.id);
                            }}
                            sx={successBtnSx}
                          >
                            {t("codSalesManager.approveAndPost")}
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Box>
        </Box>
      </Box>

      <Dialog
        open={rejectDialog.open}
        onClose={() =>
          !actionLoading &&
          setRejectDialog({ open: false, orderId: "", orderNumber: "" })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("codSalesManager.rejectOrder")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("codSalesManager.rejectOrderHint", {
              order: rejectDialog.orderNumber,
            })}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t("codSalesManager.rejectionReason")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={!!actionLoading}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            position: "sticky",
            bottom: 0,
            bgcolor: NJIM.white,
            borderTop: `1px solid ${NJIM.border}`,
          }}
        >
          <Button
            onClick={() =>
              setRejectDialog({ open: false, orderId: "", orderNumber: "" })
            }
            disabled={!!actionLoading}
            sx={secondaryBtnSx}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={!!actionLoading || !rejectReason.trim()}
            onClick={handleReject}
            sx={dangerBtnSx}
          >
            {actionLoading ? t("common.loading") : t("codSalesManager.confirmReject")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodSalesManager;
